import { PackageType } from "@revenuecat/purchases-js";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { proStatusLine } from "../lib/pro";
import { loadProDetails, type ProDetails } from "../lib/purchases";
import { useSubscriptionStore } from "../store/subscription";
import { useUIStore } from "../store/ui";
import { Button } from "./ui/Button";
import { CaliuLogo } from "./ui/CaliuLogo";
import { Dialog, DialogCloseButton, DialogContent } from "./ui/dialog";
import { ProTag } from "./ui/ProTag";
import { Icon } from "@/components/ui/Icon";

const CONTACT_HREF =
  "mailto:hola@caliuapp.com?subject=" + encodeURIComponent("Caliu Pro");

/**
 * The counterpart to `PaywallDialog`, shown to users who are already Pro
 * (opened from the settings menu's status card). Thanks them and surfaces their
 * plan, renewal/expiry, and a link to RevenueCat's billing portal when one
 * exists. Server entitlement (`expiresAt`/`willRenew`) drives the renewal line;
 * RevenueCat supplies the plan/price/portal for display only.
 */
export function ProThanksDialog() {
  const { t } = useTranslation();
  const open = useUIStore((s) => s.proThanksOpen);
  const closeProThanks = useUIStore((s) => s.closeProThanks);
  const expiresAt = useSubscriptionStore((s) => s.expiresAt);
  const willRenew = useSubscriptionStore((s) => s.willRenew);
  const source = useSubscriptionStore((s) => s.source);
  const plan = useSubscriptionStore((s) => s.plan);

  const [details, setDetails] = useState<ProDetails | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void loadProDetails().then((d) => {
      if (!cancelled) setDetails(d);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // The server's `plan` names a lifetime purchase even when RevenueCat's
  // offering no longer lists the package (e.g. bought on iOS, or the offering
  // failed to load), so it wins over the matched package type.
  const planLabel =
    plan === "lifetime" || details?.packageType === PackageType.Lifetime
      ? t("pro.lifetimePlan")
      : details?.packageType === PackageType.Annual
        ? t("pro.yearly")
        : details?.packageType === PackageType.Monthly
          ? t("pro.monthly")
          : "Pro";

  const status = proStatusLine(t, { plan, expiresAt, willRenew, source });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && closeProThanks()}>
      <DialogContent className="w-[460px] max-w-[calc(100vw-2rem)] overflow-hidden border-none p-0">
        {/* accent glow fading into the canvas (mirrors the paywall) */}
        <div
          aria-hidden
          className="pro-accent-glow pointer-events-none absolute inset-0"
        />

        <div className="absolute right-4 top-4 z-10">
          <DialogCloseButton onClick={closeProThanks} />
        </div>

        <div className="relative px-9 pb-7 pt-9">
          {/* header */}
          <div className="flex flex-col items-center text-center">
            <CaliuLogo size={56} className="text-accent" leafOpacity={0.5} />
            <h2 className="mt-4 flex items-center gap-2 font-headings text-2xl font-bold text-ink">
              Caliu
              <ProTag className="text-base" />
            </h2>
            <p className="mt-4 text-lg font-semibold text-ink">
              {t("pro.thanks")}
            </p>
            <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-muted">
              {t("pro.thanksMessage")}
            </p>
          </div>

          {/* subscription details */}
          <div className="pro-plan-panel mt-7 flex items-center gap-4 rounded-2xl px-5 py-3.5">
            <Icon name="badge-check" size={22} className="shrink-0 text-accent" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[15px] font-semibold text-ink">
                  {planLabel}
                </span>
                {details?.price ? (
                  <span className="text-[15px] font-bold text-accent">
                    {details.price}
                  </span>
                ) : (
                  <span className="rounded-full bg-accent-bg px-2.5 py-0.5 text-[11px] font-semibold text-accent-text">
                    {t("pro.active")}
                  </span>
                )}
              </div>
              {status && <p className="mt-0.5 text-[13px] text-muted">{status}</p>}
            </div>
          </div>

          {/* Post-purchase sign-in nudge: a purchase made with no account is
              tied to this device until an account carries it.

              Routes to SIGN IN, not sign-up (mirrors Swift's ProThanksSheet):
              most people reaching this card already have a Caliu account and
              want THAT one to hold the purchase, and sending them to sign-up
              made them type an email the worker then refused as taken. The
              sign-in page carries its own link to sign up. */}
          {details?.managementURL && (
            <Button
              variant="secondary"
              size="lg"
              className="mt-5 w-full"
              onClick={() =>
                window.open(
                  details.managementURL!,
                  "_blank",
                  "noopener,noreferrer",
                )
              }
            >
              {t("pro.manageSubscription")}
            </Button>
          )}

          {/* Grandfathered early supporters have free Pro for life: explain
              (mirrors Swift's ProThanksSheet grandfathered note). The manage
              button above still shows when they also pay for a subscription,
              which their grant does not stop billing. */}
          {source === "grandfathered" && (
            <p className="mx-auto mt-5 max-w-xs text-center text-[13px] leading-relaxed text-muted">
              {t("pro.grandfatheredNote")}
            </p>
          )}

          <div className="mt-6 text-center">
            <a
              href={CONTACT_HREF}
              className="text-[13px] font-medium text-muted underline underline-offset-4 hover:text-ink"
            >
              {t("pro.contactUs")}
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
